import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "./config";
import { ErrorResponsesCreateOrUpdate, Record } from "../types/records.types";
import { MutationOptions } from "../types/mutation.types";

export const useUpdateRecord = (options?: MutationOptions) => {
  const queryClient = useQueryClient();
  return useMutation<any, ErrorResponsesCreateOrUpdate, Record, any>({
    mutationFn: (record: Record) => updateRecord(record),
    onSuccess(data, variables) {
      const entity = variables.entity;
      const updatedId = data?.id || variables.id;
      const recordsKey = options?.queryInvalidatePrefix || `${entity}-records`;
      const relatedRecordsKey = `${entity}-items-parentId`;
      const recordKey = `${entity}-${updatedId}`;

      const matchesListQuery = (queryKey: string) => {
        const matchesRecords = queryKey.includes(recordsKey);
        const matchesRelated = queryKey.includes(relatedRecordsKey);
        return matchesRecords || matchesRelated;
      };

      if (!options?.skipOptimisticUpdate) {
        queryClient.setQueriesData(
          { predicate: (query: any) => matchesListQuery(query.queryKey[0]) },
          (oldData: any) => {
            if (!oldData?.items) return oldData;
            const updatedItems = oldData.items.map((item: any) =>
              String(item.id) === String(updatedId) ? { ...item, ...variables.data } : item
            );
            return { ...oldData, items: updatedItems };
          }
        );
      }

      const shouldInvalidateList = options?.invalidateListQuery !== false;
      const shouldInvalidateItem = options?.invalidateItemQuery !== false;

      const invalidationKeys = [
        ...(shouldInvalidateList ? [recordsKey, relatedRecordsKey] : []),
        ...(shouldInvalidateItem ? [recordKey] : []),
      ];

      invalidationKeys.forEach((key) => {
        queryClient.invalidateQueries({
          predicate: (query: any) => query.queryKey[0].includes(key),
        });
      });
    },
    onError(error: ErrorResponsesCreateOrUpdate) {
      return error.response.data.errors[0].message;
    },
  });
};

export const updateRecord = async (record: Record) => {
  const data = record.data;
  const fields = record.fields ? { fields: record.fields } : undefined;

  const isClientsEntity = record.entity === "clients";
  const entityData = isClientsEntity ? removeVirtualUserFields(data) : data;

  const updatedRecord = await api({
    method: "PATCH",
    url: `items/${record.entity}/${record.id}`,
    data: entityData,
    params: fields,
  });

  if (isClientsEntity) {
    const userFields = extractUserFields(data);

    if (Object.keys(userFields).length > 0) {
      const userId = record.recordContext?.item.user_id?.id;

      await api({
        method: "PATCH",
        url: `/users/${userId}`,
        data: userFields,
      });
    }
  }

  return updatedRecord.data.data;
};


const VIRTUAL_USER_FIELDS = ["user_avatar", "user_first_name", "user_last_name", "user_email", "user_phone", "client_user_id"];

const removeVirtualUserFields = (data: { [key: string]: any }) => {
  const filteredData = Object.fromEntries(
    Object.entries(data).filter(([key]) => !VIRTUAL_USER_FIELDS.includes(key))
  );
  return filteredData;
};

const extractUserFields = (data: { [key: string]: any }) => {
  const userPrefix = "user_";
  const userFieldNames = VIRTUAL_USER_FIELDS.filter((field) => field.startsWith(userPrefix));

  const result: { [key: string]: any } = {};
  for (const fieldName of userFieldNames) {
    if (fieldName in data) {
      const directusFieldName = fieldName.replace(userPrefix, "");
      result[directusFieldName] = data[fieldName];
    }
  }

  return result;
};
